'use client';

import React, { useContext } from 'react';
import Link from 'next/link';
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { IdeasTable } from '../_components/IdeasTable';
import { uniqBy } from 'lodash';
import {
  RefreshCwIcon,
  TrendingUpIcon,
  UsersIcon,
  HeartIcon,
  VideoIcon,
  LightbulbIcon,
  CreditCardIcon,
  CrownIcon,
  UserIcon,
  AlertTriangleIcon,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../_components/Button';
import { useState } from 'react';
import { useSession } from 'next-auth/react';
import { PricingModal } from '../_components/PricingModal';
import { useContentLogList, useStatsLogList } from '../lib/content';
import { AppStateContext } from '../_components/Providers';
import { PlatformEnum } from '@/server/utils/platform-enum';

export default function DashboardPage() {
  const session = useSession();
  const [timeFilter, setTimeFilter] = useState<'week' | 'day'>('week');
  const [chartFilters, setChartFilters] = useState({
    followers: true,
    videos: true,
    likes: true,
  });
  const [showPricingModal, setShowPricingModal] = useState(false);
  const { platformFilter } = useContext(AppStateContext);

  const {
    contentLogs,
    isPending: isPendingContentLogs,
    isFetching: isFetchingContentLogs,
    reset: resetContentLogs,
  } = useContentLogList(platformFilter, {});
  const {
    statsLogs,
    query: {
      isPending: isPendingStatsLogs,
      isFetching: isFetchingStatsLogs,
      refetch: refetchStatsLogs,
    },
  } = useStatsLogList(platformFilter, {});

  // Filter stats based on time filter
  const uniqueStatsLogs = uniqBy(statsLogs, timeFilter === 'week' ? 'week' : 'day');
  const reverseStatsLogs = uniqueStatsLogs.slice();
  reverseStatsLogs.reverse();

  const latestStats = uniqueStatsLogs.find(Boolean);
  const oneWeekAgoStats = uniqueStatsLogs.slice(1, 2).find(Boolean);

  const latestFollowers = latestStats?.data.followers || 0;
  const oneWeekAgoFollowers = oneWeekAgoStats?.data.followers || 0;

  const latestLikes = latestStats?.data.likes || 0;
  const oneWeekAgoLikes = oneWeekAgoStats?.data.likes || 0;

  const latestVideos = latestStats?.data.videos || 0;
  const oneWeekAgoVideos = oneWeekAgoStats?.data.videos || 0;

  const chartData = reverseStatsLogs.map((log, i) => ({
    name: timeFilter === 'week' ? `Week ${i + 1}` : `Day ${i + 1}`,
    followers: log.data.followers,
    videos: log.data.videos,
    likes: log.data.likes,
  }));

  const kpis = [
    {
      name: platformFilter === PlatformEnum.Reddit ? 'Subscribers' : 'Followers',
      value: latestFollowers || '-',
      change: oneWeekAgoStats ? latestFollowers - oneWeekAgoFollowers : 0,
      icon: UsersIcon,
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      name: platformFilter === PlatformEnum.Reddit ? 'Karma' : 'Likes',
      value: latestLikes || '-',
      change: oneWeekAgoStats ? latestLikes - oneWeekAgoLikes : 0,
      icon: HeartIcon,
      gradient: 'from-pink-500 to-rose-500',
    },
    {
      name: platformFilter === PlatformEnum.Reddit ? 'Posts' : 'Videos',
      value: latestVideos || '-',
      change: oneWeekAgoStats ? latestVideos - oneWeekAgoVideos : 0,
      icon: VideoIcon,
      gradient: 'from-purple-500 to-indigo-500',
    },
  ];

  // Get current plan information
  const currentPlan = session.data?.user?.plan || null;
  const getPlanInfo = (plan: string | null) => {
    switch (plan) {
      case 'starter':
        return { name: 'Starter', price: '$9/mo', gradient: 'from-green-500 to-emerald-500' };
      case 'pro':
        return { name: 'Pro', price: '$29/mo', gradient: 'from-blue-500 to-cyan-500' };
      case 'studio':
        return { name: 'Studio', price: '$149/mo', gradient: 'from-purple-500 to-pink-500' };
      default:
        return { name: 'Free', price: '$0/mo', gradient: 'from-gray-500 to-slate-500' };
    }
  };

  const planInfo = getPlanInfo(currentPlan);

  // Check if user has access to day filter (pro and studio plans only)
  const hasDayFilterAccess = currentPlan === 'pro' || currentPlan === 'studio';

  // Check if user has a completed profile (purpose and hook are required)
  const userProfile = session.data?.user?.profile;
  const hasCompletedProfile = userProfile?.purpose && userProfile?.hook;

  // Check if user has access to ideas (paid plans only AND completed profile)
  const hasIdeasAccess =
    (currentPlan === 'starter' || currentPlan === 'pro' || currentPlan === 'studio') &&
    hasCompletedProfile;

  // If user doesn't have access to day filter and it's currently selected, reset to week
  React.useEffect(() => {
    if (!hasDayFilterAccess && timeFilter === 'day') {
      setTimeFilter('week');
    }
  }, [hasDayFilterAccess, timeFilter]);

  // Don't render anything while session is loading
  if (session.status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-blue-500/20 to-cyan-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 p-3 sm:p-6">
        {/* Header */}
        <section className="flex flex-row gap-4 items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
              Dashboard
            </h1>
            <p className="text-gray-400 mt-1">Track your content performance</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Time Filter Toggle */}
            <div className="flex items-center gap-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg p-1">
              <button
                onClick={() => setTimeFilter('week')}
                className={cn(
                  'px-3 py-1 rounded-md text-xs font-medium transition-all duration-200',
                  timeFilter === 'week'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'text-gray-400 hover:text-white'
                )}
              >
                Week
              </button>
              <button
                onClick={() => hasDayFilterAccess && setTimeFilter('day')}
                disabled={!hasDayFilterAccess}
                className={cn(
                  'px-3 py-1 rounded-md text-xs font-medium transition-all duration-200 relative group',
                  timeFilter === 'day' && hasDayFilterAccess
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : hasDayFilterAccess
                      ? 'text-gray-400 hover:text-white'
                      : 'text-gray-500 cursor-not-allowed opacity-50'
                )}
                title={
                  !hasDayFilterAccess
                    ? 'Upgrade to Pro or Studio plan to access daily analytics'
                    : ''
                }
              >
                Day
                {!hasDayFilterAccess && (
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full"></div>
                )}
                {/* Upgrade tooltip for restricted users */}
                {!hasDayFilterAccess && (
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-medium rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                    <div className="flex items-center gap-2">
                      <CrownIcon className="w-3 h-3" />
                      <span>Upgrade to Pro for daily analytics</span>
                    </div>
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-orange-500"></div>
                  </div>
                )}
              </button>
            </div>
            <button
              className="group relative px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg hover:bg-white/20 transition-all duration-300 hover:scale-105"
              onClick={() => {
                refetchStatsLogs();
              }}
            >
              <RefreshCwIcon
                className={cn(
                  'w-4 h-4 text-white',
                  (isPendingStatsLogs || isFetchingStatsLogs) && 'animate-spin'
                )}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
          </div>
        </section>

        {/* Subscription Plan Section */}
        <section className="mb-8">
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-3 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl bg-gradient-to-br ${planInfo.gradient}`}>
                  <CrownIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{planInfo.name} Plan</h3>
                  <p className="text-gray-400 text-sm">{planInfo.price}</p>
                </div>
              </div>
              <Link
                href="/api/billing"
                className="group flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg hover:bg-white/20 transition-all duration-300 hover:scale-105"
              >
                <CreditCardIcon className="w-4 h-4 text-white" />
                <span className="text-sm text-white">Manage</span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Link>
            </div>
          </div>
        </section>

        {/* KPIs */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {isPendingStatsLogs
            ? // Loading skeleton for KPIs
              Array.from({ length: 3 }).map((_, i) => {
                const gradients = [
                  'from-blue-500/30 to-cyan-500/30',
                  'from-pink-500/30 to-rose-500/30',
                  'from-purple-500/30 to-indigo-500/30',
                ];
                const colors = ['bg-blue-500/20', 'bg-pink-500/20', 'bg-purple-500/20'];
                return (
                  <div
                    key={i}
                    className="group relative bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6"
                  >
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <div
                          className={`p-3 rounded-xl bg-gradient-to-br ${gradients[i]} animate-pulse`}
                        >
                          <div className={`w-6 h-6 ${colors[i]} rounded`}></div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-1">
                            <div className={`w-4 h-4 ${colors[i]} rounded animate-pulse`}></div>
                            <div className={`w-8 h-4 ${colors[i]} rounded animate-pulse`}></div>
                          </div>
                          <div className={`w-12 h-3 ${colors[i]} rounded animate-pulse`}></div>
                        </div>
                      </div>
                      <div className={`w-20 h-4 ${colors[i]} rounded animate-pulse mb-1`}></div>
                      <div className={`w-24 h-8 ${colors[i]} rounded animate-pulse`}></div>
                    </div>
                  </div>
                );
              })
            : kpis.map((kpi, i) => {
                const IconComponent = kpi.icon;
                const isPositive = kpi.change >= 0;
                return (
                  <div
                    key={i}
                    className="group relative bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/20"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-4">
                        <div className={`p-3 rounded-xl bg-gradient-to-br ${kpi.gradient}`}>
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <div className="flex items-center gap-1">
                            <TrendingUpIcon
                              className={cn(
                                'w-4 h-4',
                                isPositive ? 'text-green-400' : 'text-red-400'
                              )}
                            />
                            <span
                              className={cn(
                                'text-sm font-medium',
                                isPositive ? 'text-green-400' : 'text-red-400'
                              )}
                            >
                              {isPositive ? '+' : ''}
                              {kpi.change}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {timeFilter === 'week' ? 'this week' : 'today'}
                          </p>
                        </div>
                      </div>
                      <p className="text-gray-400 text-sm font-medium mb-1">{kpi.name}</p>
                      <p className="text-3xl font-bold text-white">{kpi.value.toLocaleString()}</p>
                    </div>
                  </div>
                );
              })}
        </section>

        {/* Performance Chart */}
        <section className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-3 sm:p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500">
                <UserIcon className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Channel Growth</h2>
            </div>
            {/* Chart Line Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setChartFilters(prev => ({ ...prev, followers: !prev.followers }))}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 relative overflow-hidden',
                  chartFilters.followers
                    ? 'bg-blue-500/30 text-blue-200 border-2 border-blue-400 shadow-lg shadow-blue-500/25'
                    : 'bg-white/5 text-gray-400 border-2 border-white/10 hover:text-white hover:bg-white/10'
                )}
              >
                <div
                  className={cn(
                    'w-3 h-3 rounded-full transition-all duration-200',
                    chartFilters.followers
                      ? 'bg-blue-400 shadow-sm shadow-blue-400/50'
                      : 'bg-blue-500/50'
                  )}
                ></div>
                <span
                  className={cn(
                    'font-semibold',
                    chartFilters.followers ? 'text-blue-100' : 'text-gray-400'
                  )}
                >
                  Followers
                </span>
                {chartFilters.followers && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-transparent"></div>
                )}
              </button>
              <button
                onClick={() => setChartFilters(prev => ({ ...prev, videos: !prev.videos }))}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 relative overflow-hidden',
                  chartFilters.videos
                    ? 'bg-purple-500/30 text-purple-200 border-2 border-purple-400 shadow-lg shadow-purple-500/25'
                    : 'bg-white/5 text-gray-400 border-2 border-white/10 hover:text-white hover:bg-white/10'
                )}
              >
                <div
                  className={cn(
                    'w-3 h-3 rounded-full transition-all duration-200',
                    chartFilters.videos
                      ? 'bg-purple-400 shadow-sm shadow-purple-400/50'
                      : 'bg-purple-500/50'
                  )}
                ></div>
                <span
                  className={cn(
                    'font-semibold',
                    chartFilters.videos ? 'text-purple-100' : 'text-gray-400'
                  )}
                >
                  Videos
                </span>
                {chartFilters.videos && (
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-transparent"></div>
                )}
              </button>
              <button
                onClick={() => setChartFilters(prev => ({ ...prev, likes: !prev.likes }))}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-200 relative overflow-hidden',
                  chartFilters.likes
                    ? 'bg-pink-500/30 text-pink-200 border-2 border-pink-400 shadow-lg shadow-pink-500/25'
                    : 'bg-white/5 text-gray-400 border-2 border-white/10 hover:text-white hover:bg-white/10'
                )}
              >
                <div
                  className={cn(
                    'w-3 h-3 rounded-full transition-all duration-200',
                    chartFilters.likes
                      ? 'bg-pink-400 shadow-sm shadow-pink-400/50'
                      : 'bg-pink-500/50'
                  )}
                ></div>
                <span
                  className={cn(
                    'font-semibold',
                    chartFilters.likes ? 'text-pink-100' : 'text-gray-400'
                  )}
                >
                  Likes
                </span>
                {chartFilters.likes && (
                  <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 to-transparent"></div>
                )}
              </button>
            </div>
          </div>
          {isPendingStatsLogs ? (
            // Loading skeleton for chart
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <defs>
                    <linearGradient id="followersGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="videosGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="likesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ec4899" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#ec4899" stopOpacity={0.1} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="name"
                    stroke="#94a3b8"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#94a3b8"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    domain={['dataMin - 10%', 'dataMax + 10%']}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(15, 23, 42, 0.9)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      backdropFilter: 'blur(12px)',
                    }}
                  />
                  {chartFilters.followers && (
                    <Line
                      type="monotone"
                      dataKey="followers"
                      stroke="#3b82f6"
                      strokeWidth={3}
                      dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
                    />
                  )}
                  {chartFilters.videos && (
                    <Line
                      type="monotone"
                      dataKey="videos"
                      stroke="#8b5cf6"
                      strokeWidth={3}
                      dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#8b5cf6', strokeWidth: 2 }}
                    />
                  )}
                  {chartFilters.likes && (
                    <Line
                      type="monotone"
                      dataKey="likes"
                      stroke="#ec4899"
                      strokeWidth={3}
                      dot={{ fill: '#ec4899', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#ec4899', strokeWidth: 2 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        {/* Recent Ideas */}
        {hasIdeasAccess ? (
          <section className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-3 sm:p-8 mb-8">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500">
                  <LightbulbIcon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white">Intelligent Content Ideas</h2>
              </div>
              <button
                className="group relative px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg hover:bg-white/20 transition-all duration-300 hover:scale-105"
                onClick={() => {
                  resetContentLogs();
                }}
              >
                <RefreshCwIcon
                  className={cn(
                    'w-4 h-4 text-white',
                    (isPendingContentLogs || isFetchingContentLogs) && 'animate-spin'
                  )}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-pink-500/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </div>
            {isPendingContentLogs ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl">
                <IdeasTable items={contentLogs.slice(0, 5)} userPlan={currentPlan} />
              </div>
            )}
          </section>
        ) : !hasCompletedProfile ? (
          // Profile incomplete - show profile completion message (for all users)
          <section className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-3 sm:p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500">
                  <LightbulbIcon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white">Intelligent Content Ideas</h2>
              </div>
              <div className="flex items-center gap-2">
                <AlertTriangleIcon className="w-4 h-4 text-yellow-400" />
                <span className="text-sm text-yellow-400 font-medium">Profile Required</span>
              </div>
            </div>
            <div className="text-center py-12">
              <div className="mb-4">
                <LightbulbIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">
                  Complete Your Profile to Access Ideas
                </h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  To generate personalized content ideas, you need to complete your profile with
                  your purpose and hook. This helps our AI understand your goals and create relevant
                  content suggestions.
                </p>
              </div>
              <Link href="/dashboard/profile">
                <Button variant="cta">
                  <UserIcon className="w-5 h-5" />
                  Complete Profile
                </Button>
              </Link>
            </div>
          </section>
        ) : !currentPlan || currentPlan === 'free' ? (
          // Free plan with complete profile - show upgrade message
          <section className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-3 sm:p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500">
                  <LightbulbIcon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-white">Intelligent Content Ideas</h2>
              </div>
              <div className="flex items-center gap-2">
                <CrownIcon className="w-4 h-4 text-orange-400" />
                <span className="text-sm text-orange-400 font-medium">Upgrade Required</span>
              </div>
            </div>
            <div className="text-center py-12">
              <div className="mb-4">
                <LightbulbIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">
                  Unlock Intelligent Content Ideas
                </h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  Get personalized content ideas powered by AI to boost your {platformFilter}{' '}
                  performance. Upgrade to any paid plan to access this feature.
                </p>
              </div>
              <Button variant="cta" onClick={() => setShowPricingModal(true)}>
                <CrownIcon className="w-5 h-5" />
                Upgrade Plan
              </Button>
            </div>
          </section>
        ) : null}

        {/* CTA Button */}
        {hasIdeasAccess && contentLogs.length > 10 && (
          <div className="text-center mb-6">
            <Link href="/dashboard/ideas">
              <Button variant="cta">
                <LightbulbIcon className="w-5 h-5" />
                View All Ideas
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Pricing Modal */}
      <PricingModal isOpen={showPricingModal} onClose={() => setShowPricingModal(false)} />
    </div>
  );
}
